import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * English and French, with no i18n library.
 *
 * Two languages and one screen's worth of copy do not need i18next, its plugin
 * chain, its async loaders or its 40 kB — all of which would land on a phone on
 * mobile data. A typed catalog does the same job and does one thing better: a
 * missing or misspelled French string is a **compile error**, not a silent
 * fallback to English that nobody notices until a patient in Quebec does.
 *
 * French is required, not optional. New Brunswick is officially bilingual and
 * Quebec's Charter obliges a business to serve in French — a pharmacy booking
 * page that cannot is one those pharmacies cannot use.
 */

export const LOCALES = ['en', 'fr'] as const;
export type Locale = (typeof LOCALES)[number];

/** `Intl` wants a region; the site serves Canada. */
export const intlLocale = (locale: string): string => (locale === 'fr' ? 'fr-CA' : 'en-CA');

const en = {
  // Chrome
  'app.title': 'Book an appointment',
  'app.skipToContent': 'Skip to main content',
  'app.language': 'Language',
  'app.poweredBy': 'Appointments by OneRx',
  'app.offline': "You're offline. Nothing can be booked or changed until you reconnect.",

  // Steps
  'step.service': 'Service',
  'step.location': 'Branch',
  'step.time': 'Time',
  'step.details': 'Your details',
  'step.review': 'Review',
  'step.questions': 'Questions',
  'step.people': 'People',
  'step.progress': 'Step {current} of {total}',
  'step.back': 'Back',
  'step.continue': 'Continue',

  // Service
  'service.heading': 'What would you like to book?',
  // Track BCW — booking by condition (BCW-D2, D5)
  'condition.heading': 'What do you need help with?',
  'condition.help': 'Choose what brings you in, or choose a service below.',
  'condition.chooseService': 'How would you like to be seen for {condition}?',
  'condition.allServices': 'See all services',
  // Track BCW — walk-ins and the waitlist (BCW-D6 to D12)
  'walkin.heading': 'Walk in, no appointment needed',
  'walkin.lead': 'Come to the pharmacy during the hours below.',
  'walkin.hoursFor': 'Walk-in hours at {location}',
  'walkin.today': 'Today',
  'walkin.closedToday': 'No walk-ins today.',
  'walkin.none': 'No walk-in hours this week. Please call the pharmacy.',
  'walkin.directions': 'Directions',
  'walkin.call': 'Call {phone}',
  'walkin.orWalkIn': 'Or walk in today: {hours}.',
  'waitlist.cta': 'None of these work? Join the waitlist',
  'waitlist.heading': 'Join the waitlist for {service}',
  'waitlist.lead': 'Tell us when you can come. When a time opens up, we’ll offer it to you.',
  'waitlist.onlyLead': 'This service isn’t booked online: join the waitlist and we’ll offer you a time.',
  'waitlist.branch': 'Branch',
  'waitlist.anyBranch': 'Any branch',
  'waitlist.days': 'Days you can come',
  'waitlist.daysHelp': 'Leave them all unticked for any day.',
  'waitlist.parts': 'Times of day',
  'waitlist.partsHelp': 'Leave them all unticked for any time.',
  'waitlist.part.morning': 'Morning (before noon)',
  'waitlist.part.afternoon': 'Afternoon (noon to 5)',
  'waitlist.part.evening': 'Evening (after 5)',
  'waitlist.until': 'Keep me on the list until',
  'waitlist.untilHelp': 'Up to {days} days.',
  'waitlist.contactHeading': 'Who are you, and how can we reach you?',
  'waitlist.contactHelp': 'Give a mobile number, an email, or both.',
  'waitlist.textOffers': 'Text me when a time opens up',
  'waitlist.join': 'Join the waitlist',
  'waitlist.contactRequired': 'Give a mobile number or an email.',
  'waitlist.doneHeading': 'You’re on the waitlist',
  'waitlist.doneLead': 'We’ll offer you a time for {service} as soon as one that suits you opens up.',
  'waitlist.already': 'You were already on this waitlist. Nothing has changed.',
  'waitlist.by.text': 'We’ll text you.',
  'waitlist.by.email': 'We’ll email you.',
  'waitlist.by.text_and_email': 'We’ll text and email you.',
  'waitlist.holdNote': 'An offered time is kept for you only a short while, so answer quickly.',
  'waitlist.manage': 'See or leave the waitlist',
  'waitlist.when': 'Times you can come',
  'waitlist.endsOn': 'On the list until',
  'waitlist.where': 'Branch',
  'waitlist.anyTime': 'Any time',
  'entry.heading': 'Your place on the waitlist',
  'entry.status.waiting': 'You’re on the waitlist.',
  'entry.status.offered': 'We’ve offered you a time. Check your texts or email.',
  'entry.status.paused': 'Your place on the waitlist is paused.',
  'entry.status.booked': 'You’ve booked a time, so you’re no longer on the waitlist.',
  'entry.status.left': 'You’ve left the waitlist.',
  'entry.status.expired': 'Your place on the waitlist has ended.',
  'entry.status.removed': 'The pharmacy has closed this place on the waitlist.',
  'entry.keep': 'Keep me on the list',
  'entry.pausedCall': 'To keep your place, please call the pharmacy.',
  'entry.leave': 'Leave the waitlist',
  'entry.leaveQuestion': 'Leave the waitlist? We won’t offer you times any more.',
  'entry.leaveConfirm': 'Yes, leave the waitlist',
  'entry.stay': 'Stay on the list',
  'entry.missing': 'This link is not valid any more.',
  'offer.heading.held': 'A time is held for you',
  'offer.heading.group': 'A time just opened up',
  'offer.heading.closed': 'Your waitlist offer',
  'offer.heldUntil': 'We’re holding it for you until {until}. Book it or say no thanks before then.',
  'offer.groupLead':
    'We’ve offered this time to a few people on the waitlist. The first to book it gets it, until {until}.',
  'offer.lastMinute': 'Less than a minute left.',
  'offer.questionsFirst': 'This visit asks a few questions before you book.',
  'offer.answerQuestions': 'Answer the questions',
  'offer.book': 'Book it',
  'offer.decline': 'No thanks',
  'offer.formsFailed': 'We couldn’t load the questions for this visit.',
  'offer.retry': 'Try again',
  'offer.formsCall': 'Please call the pharmacy to book this time.',
  'offer.consentNeeded': 'Please read and accept the consent statement before booking.',
  'offer.backToOffer': 'Back to the offer',
  'offer.closed.booked': 'You’ve booked this time.',
  'offer.closed.declined': 'You said no thanks to this time.',
  'offer.closed.lapsed': 'This offer has ended.',
  'offer.closed.taken': 'Someone else booked this time first.',
  'offer.closed.withdrawn': 'This offer was withdrawn.',
  'offer.stillOn': 'You’re still on the waitlist.',
  'offer.left': 'You’ve left the waitlist. We won’t offer you times any more.',
  'service.duration': '{minutes} minutes',
  'service.none': "This pharmacy isn't taking online bookings right now.",
  'service.englishOnly': '(in English)',
  'service.methodInPerson': 'In person',
  'service.methodPhone': 'By phone',
  'service.methodVirtual': 'Video call',
  'service.methodWalkIn': 'Walk in',
  'service.search': 'Search services',
  'service.searchPlaceholder': 'Search services and conditions',
  'service.searchClear': 'Clear search',
  'service.noMatch': 'No services match "{query}".',
  'service.countOne': '1 service',
  'service.countMany': '{count} services',
  'service.category.vaccination': 'Vaccines & injections',
  'service.category.travel': 'Travel health',
  'service.category.minor_ailment': 'Minor ailments',
  'service.category.medication_review': 'Medication reviews',
  'service.category.testing': 'Tests & screening',
  'service.category.consult': 'Consultations',
  'service.category.other': 'Other services',

  // Location
  'location.heading': 'Which branch?',

  // Time
  'time.heading': 'Pick a time',
  'time.headingFor': 'Pick a time for {service}',
  'time.nextAvailable': 'Next available: {when}',
  'time.noneSoon': 'No times in the next 14 days.',
  'time.noneSoonCall': 'No times in the next 14 days. Call {phone} to book.',
  'time.loadFailed': "We couldn't load the times.",
  'time.date': 'Date',
  'time.dateHelp': 'Type a date or use the calendar.',
  'time.loading': 'Looking for open times…',
  'time.none': 'No open times on that day. Try another date.',
  'time.available': '{count} times available',
  'time.availableOne': '1 time available',
  'time.timezoneNote': 'Times shown for {location}.',
  'time.selected': 'Selected {time}',
  'time.gridLabel': 'Available times. Use the arrow keys to move between them.',
  'time.chooseDay': 'Choose a day',
  'time.earlierDays': 'Earlier days',
  'time.laterDays': 'Later days',
  'time.today': 'Today',
  'time.dayOpen': '{day}, times available',
  'time.dayClosed': '{day}, no open times',
  'time.part.morning': 'Morning',
  'time.part.afternoon': 'Afternoon',
  'time.part.evening': 'Evening',

  // Details
  'details.heading': 'Who is the appointment for?',
  'details.aboutYou': 'About you',
  'details.reachYou': 'How we can reach you',
  'details.firstName': 'First name',
  'details.lastName': 'Last name',
  'details.dob': 'Date of birth',
  'details.dobHelp': 'We ask because some services have an age requirement.',
  'details.email': 'Email',
  'details.emailHelp': 'So the pharmacy can reach you about this appointment.',
  'details.phone': 'Mobile number',
  'details.province': 'Province',
  'details.healthCard': 'Health card number (optional)',
  'details.healthCardHelp':
    'Stored securely. Leave blank and bring your card instead if you prefer.',
  'details.required': 'required',
  'details.holdTimer': 'Your time is held for less than a minute more. Confirm now to keep it.',
  'details.holdExpired': 'Your time was released. Please choose another.',
  'details.textReminders': 'Text me appointment reminders at this number',
  'details.textRemindersAgree': 'By ticking the box, you agree to the following:',

  // Consent
  'consent.heading': 'Consent',
  'consent.agree': 'I have read and agree to the above.',

  // Review / submit
  'review.heading': 'Review your booking',
  'review.confirm': 'Confirm booking',
  'review.change': 'Change',
  'review.submitting': 'Booking…',
  'review.who': 'Who',
  'review.contact': 'Contact',
  'review.consent': 'You agreed to',
  'review.texts': 'Text reminders',
  'review.textsYes': 'Yes, at {phone}',
  'review.textsNo': 'No',
  'review.minutes': '{minutes} min',
  'review.forms': 'Your answers',
  'review.formAnswered': 'Answered',
  'review.formSkipped': 'Skipped: we’ll send you a link after you book',
  'review.formChange': 'Change your answers to {form}',
  'review.formsGo': 'Your answers go to {pharmacy} with your booking.',
  'review.formsChanged': 'The questions for this service changed. Please answer them before you book.',
  'review.formsUnavailable':
    'Forms can’t be taken right now. You can still book, and the pharmacy will send them to you later.',
  'questions.formOf': 'Form {current} of {total}',
  'questions.required': 'Needed to book online',
  'questions.optional': 'Optional',
  'questions.optionalIntro':
    'This form is optional. You can answer it now, or skip it and we’ll send you a link after you book.',
  'questions.answerNow': 'Answer now',
  'questions.skip': 'Skip for now',
  'questions.continue': 'Continue',
  'questions.otherService': 'Choose another service',
  'questions.loading': 'Loading the questions for this service…',
  'questions.loadFailed': 'We couldn’t load the questions for this service.',
  'questions.retry': 'Try again',
  'questions.timedOut':
    'For your privacy, your answers were cleared after 20 minutes without activity. Please start again.',
  'error.summary': 'Please fix the following:',

  // Done
  'done.heading': 'You are booked',
  'done.when': 'When',
  'done.where': 'Where',
  'done.what': 'What',
  'done.emailed': "We've sent a confirmation to {email}.",
  'done.saveThis': 'Save this page or take a screenshot — it has your booking details.',
  'done.manageLink': 'Change or cancel',

  // Manage
  'manage.heading': 'Your appointment',
  'manage.reschedule': 'Change time',
  'manage.rescheduleHeading': 'Pick a new time',
  'manage.useThisTime': 'Change to this time',
  'manage.keepTime': 'Keep my time',
  'manage.moved': 'Done — your appointment is now {when}.',
  'manage.cancel': 'Cancel appointment',
  'manage.cancelConfirm': "Cancel this appointment? This can't be undone.",
  'manage.cancelReason': 'Reason (optional)',
  'manage.cancelled': 'This appointment is cancelled.',
  'manage.completed': 'This appointment is done.',
  'manage.noReschedule': 'To change this appointment, please call the pharmacy.',
  'manage.keepIt': 'Keep it',

  // Patient forms (Track SFM)
  'form.loading': 'Opening your form…',
  'form.dobHeading': 'Enter your date of birth',
  'form.dobIntro':
    'To keep your answers private, we check your date of birth before showing you the form.',
  'form.dobLegend': 'Date of birth',
  'form.dobHint': 'For example, 23 4 1980',
  'form.dobDay': 'Day',
  'form.dobMonth': 'Month',
  'form.dobYear': 'Year',
  'form.dobInvalid': 'Enter your date of birth, with a day, a month and a year.',
  'form.dobMismatch': "That date of birth doesn't match what the pharmacy has.",
  'form.triesLeft': 'You can try {count} more times.',
  'form.triesLeftOne': 'You can try 1 more time.',
  'form.continue': 'Continue',
  'form.sentHeading': 'Thank you, your answers were sent',
  'form.sentBody': '{pharmacy} has your answers. You can close this page.',
  'form.nextForm': 'Fill in the next form',
  'form.nextFormIntro': 'Your visit has one more form.',
  'form.timeoutHeading': 'Are you still there?',
  'form.timeoutBody':
    'For your privacy, this form closes in 2 minutes if nothing happens. Your answers so far are saved.',
  'form.keepGoing': 'Keep going',
  'form.closeNow': 'Close the form',
  'form.closedHeading': 'This form was closed for your privacy',
  'form.closedBody': 'Your answers so far are saved. Open the form again to carry on.',
  'form.openAgain': 'Open the form again',
  'form.otherDevice':
    'This form was opened on another device. Open it again here to carry on.',
  'form.updated': 'The pharmacy updated this form, so it has reloaded. Check your answers.',
  'form.saveFailed':
    "We couldn't save your last answer. We'll try again when you move to the next question.",
  'form.callPhone': 'Questions? Call {phone}.',
  'form.callForLink': 'Call {phone} for a new link.',
  'form.callPharmacy': 'Please call the pharmacy.',
  'form.state.submitted': 'You already sent this form. Thank you.',
  'form.state.expired': 'This link has expired.',
  'form.state.locked': 'For your privacy, this link is locked after too many tries.',
  'form.state.cancelled': 'The pharmacy no longer needs this form.',
  'form.state.not_needed':
    'This form is no longer needed: the appointment it was for was cancelled.',
  'form.state.replaced': 'A newer link was sent for this form. Please use the latest one.',
  'form.state.used': 'This code was already used on another phone.',
  'form.state.restart':
    "For your privacy, this form was closed. Open the pharmacy's form link again to start over.",
  'form.state.unavailable': 'This form is not available right now.',
  'form.state.missing': 'We could not find this form. Check the link.',
  'form.publicIntro':
    'First, tell us who you are, so the pharmacy can add your answers to your record.',
  'form.contactHint': 'Give a mobile number or an email, or both, so the pharmacy can reach you.',
  'form.contactRequired': 'Give a mobile number or an email.',
  'form.start': 'Start the form',
  'form.spamCheck': 'Check that you are not a robot',
  'form.spamFailed': 'Please complete the check that you are not a robot.',
  'form.dailyCap': 'This form is not taking more answers today. Please call the pharmacy.',
  'form.notAccepted': 'This form could not be started. Please call the pharmacy.',
  'form.dueHeading': 'Before your visit',
  'form.dueIntro': 'The pharmacy asks you to fill in a form before your appointment.',
  'form.dueIntroMany': 'The pharmacy asks you to fill in these forms before your appointment.',
  'form.fillIn': 'Fill in the form',
  'form.carryOn': 'Carry on with the form',
  'form.dueRequired': 'Needed before your visit',
  'form.dueOptional': 'Optional',
  'form.dueSent': 'Sent',
  'form.dueClosed': 'No longer open',
  'form.sentWithBooking': 'Sent with your booking',
  'form.formsHeading': 'Your forms',
  'form.stopAfterHeading': 'Please read this',
  'form.stopAfterCall': 'Questions? Call {phone}.',

  // Errors
  'error.missing': 'We could not find that page.',
  'error.notTaking': "This pharmacy isn't taking online bookings right now.",
  'error.missingHelp': 'Check the address, or contact the pharmacy directly.',
  'error.offline': 'We could not reach the pharmacy. Check your connection.',
  'error.slotGone': 'Someone just took that time. Please pick another.',
  'error.slotGoneKept':
    'Someone took that time while you were answering. Your answers are kept: please pick another time.',
  'error.unknown': 'Something went wrong. Please try again.',
  'error.retry': 'Try again',
  'error.fieldRequired': 'This is required.',
  'error.emailInvalid': 'Enter an email address like name@example.com.',
  'error.phoneInvalid': 'Enter a mobile number we can reach you on.',
  'error.dobInvalid': 'Enter a date of birth like 1985-04-12.',
  'error.dobFuture': 'That date is in the future.',
  'error.consentRequired': 'Please accept the consent statement to continue.',
  // Track CGS — parties (CGS-D9 to D14) and clinics (CGS-D2, D3)
  'people.heading': 'How many people?',
  'people.help': 'Book for yourself, or for you and the people coming with you, seen one after another.',
  'people.justMe': 'Just me',
  'people.count': '{count} people',
  'people.together': 'About {minutes} minutes in all',
  'people.eachOwn': "You'll give each person's details and answers, and one email and mobile for everyone.",
  'party.timeHeading': 'Pick a time for {count} people: {service}',
  'party.personStep': 'Person {n}',
  'party.contactStep': 'Contact',
  'party.personHeading': 'Person {n} of {total}',
  'party.firstPerson': 'Start with yourself, or the first person coming.',
  'party.nextPerson': 'Who else is coming?',
  'party.questionsFor': 'For {name}, person {n} of {total}',
  'party.samePerson': 'This person is already in the booking.',
  'party.timeGone': 'That time was just taken. Pick another: everyone’s details are kept.',
  'party.aboutPerson': 'About person {n}: {message}',
  'party.contactHeading': 'How can we reach you?',
  'party.contactHelp': 'One email and mobile for everyone: the confirmation and the reminders go there.',
  'party.answersGiven': '{name}: {count} form(s) answered',
  'party.oneEmail': 'One confirmation, for everyone, goes to the email above.',
  'party.confirm': 'Book for {count} people',
  'party.bookedFor': 'Booked for {count} people',
  'clinic.runsOn': 'This clinic runs on',
  'clinic.noDatesLeft': 'This clinic has no dates left.',
  'clinic.dayFull': 'Full',
  'clinic.walkIns': 'Walk-ins welcome',
  'clinic.beforeYouCome': 'Before you come',
  'clinic.full': 'This clinic is full. Call the pharmacy, or check back for a cancellation.',
  'clinic.listHeading': 'Clinics',
  'clinic.next': 'Next: {date}',
  'manage.partyHeading': 'In this booking ({count})',
  'manage.personBooked': 'Booked',
  'manage.personCancelled': 'Cancelled',
  'manage.personMoved': 'Moved to another time',
  'manage.cancelPerson': 'Cancel {name}',
  'manage.cancelPersonConfirm': "Cancel {name}'s appointment? The others keep theirs.",
  'manage.cancelAll': 'Cancel everyone',
  'manage.cancelAllConfirm': "Cancel everyone's appointments? This can't be undone.",
  'manage.moveAll': 'Move everyone',
  'manage.partyCancelled': 'Everyone in this booking is cancelled.',
  // Track ORX — online refill, renewal and transfer requests (ORX-D1 to D13)
  'refill.title': 'Request a refill, renewal or transfer',
  'refill.loading': 'Loading…',
  'refill.step.who': 'About you',
  'refill.step.code': 'Code',
  'refill.step.request': 'Request',
  'refill.step.done': 'Done',
  'refill.whoLead': "First, tell us who you are. We'll text a 6-digit code to your mobile to check it's you.",
  'refill.mobileHelp': "We'll text your code to this number.",
  'refill.sendCode': 'Text me a code',
  'refill.tooMany': 'Too many codes were asked for. Please wait a while and try again, or call us.',
  'refill.checkDetails': 'Please check your details and try again.',
  'refill.codeHeading': 'Enter your code',
  'refill.codeSentTo': 'We texted a 6-digit code to {phone}. It expires in 10 minutes.',
  'refill.codeLabel': '6-digit code',
  'refill.codeHint': 'Type or paste the code from the text.',
  'refill.codeIncomplete': 'Enter all 6 digits of the code.',
  'refill.wrongCode': "That code isn't right. {count} tries left.",
  'refill.wrongCodeOne': "That code isn't right. 1 try left.",
  'refill.wrongCodeAgain': "That code isn't right. Please try again.",
  'refill.resend': 'Text me a new code',
  'refill.resendIn': 'You can ask for a new code in {seconds} seconds.',
  'refill.resent': 'We texted you a new code.',
  'refill.noMoreCodes': "We can't send more codes for this request. Start again later, or call us.",
  'refill.changeDetails': 'Change my details',
  'refill.locked': 'Too many wrong codes, so this code is locked.',
  'refill.expired': 'This code has expired.',
  'refill.sessionExpired': 'This request took too long and has expired. Nothing was sent.',
  'refill.startAgainHelp': "Start again and we'll text you a new code.",
  'refill.startAgain': 'Start again',
  'refill.requestHeading': 'What do you need?',
  'refill.kindLegend': 'Type of request',
  'refill.kind.refill': 'A refill',
  'refill.kind.refillHelp': 'More of a prescription you already fill with us.',
  'refill.kind.renewal': 'A renewal',
  'refill.kind.renewalHelp': 'A prescription that has run out of refills.',
  'refill.kind.transfer': 'A transfer',
  'refill.kind.transferHelp': 'Move a prescription to us from another pharmacy.',
  'refill.kindRequired': 'Choose a refill, a renewal or a transfer.',
  'refill.items.refill': 'Prescription numbers or medication names',
  'refill.items.refillHelp': 'The prescription number is on your label. One per box.',
  'refill.items.renewal': 'Medications to renew',
  'refill.items.renewalHelp': 'The medication name, or its prescription number. One per box.',
  'refill.items.transfer': 'Medications to transfer',
  'refill.items.transferHelp': 'The medication name, or its prescription number at the other pharmacy. One per box.',
  'refill.item.refill': 'Prescription or medication {n}',
  'refill.item.renewal': 'Medication to renew {n}',
  'refill.item.transfer': 'Medication to transfer {n}',
  'refill.itemRemove': 'Remove number {n}',
  'refill.itemAdd': 'Add another',
  'refill.itemsMax': 'You can ask for up to 10 at a time.',
  'refill.itemsRequired': 'Add at least one prescription number or medication.',
  'refill.fromName': "Pharmacy it's at now",
  'refill.fromPhone': "That pharmacy's phone number, if you know it",
  'refill.prescriber': 'Prescriber, if you know',
  'refill.neededBy': 'When do you need it by?',
  'refill.optional': 'Optional.',
  'refill.neededByPast': 'Choose today or a later date.',
  'refill.neededByInvalid': 'Enter a date like 2026-11-05.',
  'refill.note': 'Anything else we should know?',
  'refill.noteHelp': 'Optional. Please don’t include a health card number.',
  'refill.noteCount': '{count} of {max} characters',
  'refill.noteTooLong': 'Keep the note to 500 characters or fewer.',
  'refill.textWhenReady': "Text me when it's ready",
  'refill.send': 'Send my request',
  'refill.checkRequest': 'Please check your request and try again.',
  'refill.doneHeading': "We've sent your request to {pharmacy}",
  'refill.reference': 'Your request number',
  'refill.doneText': "We'll text you when it's ready. Call us if you have a question.",
  'refill.doneCall': "We'll call you if we have a question. Call us if you have one.",
  'refill.unavailableHeading': "Online requests aren't available here",
  'refill.callUsAt': 'To ask for a refill, a renewal or a transfer, call us at',
  'refill.callUs': 'To ask for a refill, a renewal or a transfer, please call the pharmacy.',
  // Track ORX follow-ups (2026-10-29) — no texted code (ORX-D15 to D19), the email (ORX-D20), and the
  // start's refusals said by their field
  'refill.whoLeadNoCode': 'First, tell us who you are.',
  'refill.mobileHelpNoCode': "We'll use this number if we need to reach you about your request.",
  'refill.phoneCheck': 'Your mobile number: {phone}. Is that right?',
  'refill.changeNumber': 'Change number',
  'refill.email': 'Email (optional)',
  'refill.emailHelp': "We'll email you that we got your request, and again when it's ready.",
  'refill.emailInvalid': 'Enter an email address like name@example.com.',
  'refill.doneEmailed': "We've emailed a copy to {email}.",
  'refill.nameMissing': 'Enter your first and last name.',
  'refill.nameTooLong': 'That name is too long.',
  'refill.phoneInvalid': 'Enter a Canadian or US mobile number that can get texts.',
  'refill.tooManyNoCode': 'Too many requests were started. Please wait a while and try again, or call us.',
  'refill.startAgainHelpNoCode': 'Your details are still filled in, so it only takes a moment.',
  // ORX-D24 — a mobile that once texted STOP to the pharmacy (the owner to confirm the words)
  'refill.textsStoppedCode':
    "We can't text a code to this number. If you replied STOP to {pharmacy}, reply START to their last text and try again, or use another mobile number.",
  'refill.textsStoppedNote':
    "We can't text this number. If you replied STOP to {pharmacy}, reply START to their last text. To hear when it's ready, add your email below.",
  'refill.doneEmailWhenReady': "We'll email you when it's ready. Call us if you have a question.",
  // Track CIQ — check-in at a place (its poster and the counter's kiosk), I'm here on a manage link,
  // the patient's own place in line, and the lobby screen (CIQ-D2 to D8, D16, D28)
  'checkIn.missing': "This check-in link isn't working. Please see the counter.",
  'checkIn.heading': 'Check in at {place}',
  'checkIn.lead': "Let us know you're here, and we'll save your place in line.",
  'checkIn.haveAppointment': 'I have an appointment',
  'checkIn.haveAppointmentHelp': 'Find your visit with your last name and date of birth.',
  'checkIn.walkingIn': "I'm walking in",
  'checkIn.walkingInHelp': 'No appointment? Join the line.',
  'checkIn.noWalkIns': 'No appointment? Please see the counter.',
  'checkIn.find': 'Find my visit',
  'checkIn.findLead': 'Enter the last name and date of birth of the person with the appointment.',
  'checkIn.notFound': "We couldn't find a visit today with those details.",
  'checkIn.seeCounter': 'Please see the counter.',
  'checkIn.tooEarly': 'Check-in opens at {time}, an hour before the visit.',
  'checkIn.tooLate': 'Check-in has closed for this visit. Please see the counter.',
  'checkIn.expired': 'That took a while. Please find your visit again.',
  'checkIn.foundHeading': 'We found your visit',
  'checkIn.whoIsHere': "Who's here?",
  'checkIn.alreadyIn': 'Already checked in',
  'checkIn.allIn': 'Everyone here is already checked in.',
  'checkIn.imHere': "I'm here",
  'checkIn.textMe': "Text me when it's my turn",
  'checkIn.textTo': 'At the mobile ending in {digits}.',
  'checkIn.walkInHeading': 'Join the line',
  'checkIn.walkInLead': "Tell us who you are, and we'll add you to the line.",
  'checkIn.serviceLegend': 'What are you here for?',
  'checkIn.serviceRequired': "Choose what you're here for.",
  'checkIn.questionsFirst': 'There are a few questions to answer before you join the line.',
  'checkIn.formsGo': 'Your answers go to {pharmacy} when you join the line.',
  'checkIn.formSkipped': 'Skipped',
  'checkIn.formOptionalIntro': 'This form is optional. You can answer it now, or skip it.',
  'checkIn.join': 'Join the line',
  'checkIn.walkInsClosed': "Walk-ins aren't open right now. Please see the counter.",
  'checkIn.clinicFull': 'This clinic is full today. Please see the counter.',
  'checkIn.alreadyWaiting': "You're already in line here: number {ticket}.",
  'checkIn.phoneInvalid': 'Enter a Canadian or US mobile number.',
  'checkIn.formsChanged': 'The questions for this visit changed. Please answer them before you join the line.',
  'checkIn.formsUnavailable':
    "We can't take your answers right now. You can still join the line, and the pharmacy will go over the questions with you.",
  'checkIn.visitChanged': 'This visit changed a moment ago. Please see the counter.',
  'checkIn.unavailable': "Check-in isn't open for this visit. Please see the counter.",
  'checkIn.busy': 'Lots of people are checking in right now. Please try again in a minute, or see the counter.',
  'checkIn.startOver': 'Start over',
  'checkIn.checkedIn': "You're checked in.",
  'checkIn.number': 'Your number is {ticket}',
  'checkIn.numbers': 'Your numbers are {tickets}',
  'checkIn.kioskNumber': 'Your number is {ticket} — please take a seat.',
  'checkIn.kioskNumbers': 'Your numbers are {tickets} — please take a seat.',
  'checkIn.ahead': '{n} people ahead of you',
  'checkIn.aheadOne': '1 person ahead of you',
  'checkIn.next': "You're next",
  'checkIn.follow': 'Follow your place in line',
  'checkIn.manageHeading': 'Check in for your visit',
  'checkIn.manageLead': "At the pharmacy? Let us know you're here.",
  'queue.loading': 'Finding your place in line…',
  'queue.heading': 'Your place in line',
  'queue.missing': 'This link has ended.',
  'queue.called': "It's your turn — please come to the counter.",
  'queue.serving': "You're with the pharmacist.",
  'queue.done': "You've been seen. Thank you!",
  'queue.left': 'You left the line.',
  'queue.leave': 'Leave the line',
  'queue.leaveQuestion': "Leave the line? You'll lose your place, and today's visit will be cancelled.",
  'queue.leaveConfirm': 'Yes, leave the line',
  'queue.stay': 'Stay in line',
  'queue.updates': 'This page updates on its own.',
  'lobby.loading': 'Loading…',
  'lobby.nowCalling': 'Now calling',
  'lobby.next': 'Next',
  'lobby.nobodyCalled': 'Nobody is being called',
  'lobby.nobodyWaiting': 'Nobody is waiting',
  'lobby.missing': "This screen's link has been replaced. Ask staff for the new one.",
} as const;

export type MessageKey = keyof typeof en;

/**
 * `Record<MessageKey, string>` is the whole safeguard: adding a key to English
 * without adding it here fails the build.
 */
const fr: Record<MessageKey, string> = {
  'app.title': 'Prendre rendez-vous',
  'app.skipToContent': 'Aller au contenu principal',
  'app.language': 'Langue',
  'app.poweredBy': 'Rendez-vous par OneRx',
  'app.offline':
    'Vous êtes hors ligne. Rien ne peut être réservé ni modifié avant le retour de la connexion.',

  'step.service': 'Service',
  'step.location': 'Succursale',
  'step.time': 'Heure',
  'step.details': 'Vos coordonnées',
  'step.review': 'Vérification',
  'step.questions': 'Questions',
  'step.people': 'Personnes',
  'step.progress': 'Étape {current} sur {total}',
  'step.back': 'Retour',
  'step.continue': 'Continuer',

  'service.heading': 'Que souhaitez-vous réserver ?',
  'condition.heading': 'Pour quoi avez-vous besoin d’aide ?',
  'condition.help': 'Choisissez ce qui vous amène, ou un service ci-dessous.',
  'condition.chooseService': 'Comment souhaitez-vous être vu pour : {condition} ?',
  'condition.allServices': 'Voir tous les services',
  'walkin.heading': 'Sans rendez-vous',
  'walkin.lead': 'Présentez-vous à la pharmacie aux heures ci-dessous.',
  'walkin.hoursFor': 'Heures sans rendez-vous : {location}',
  'walkin.today': 'Aujourd’hui',
  'walkin.closedToday': 'Pas de visites sans rendez-vous aujourd’hui.',
  'walkin.none': 'Aucune heure sans rendez-vous cette semaine. Veuillez appeler la pharmacie.',
  'walkin.directions': 'Itinéraire',
  'walkin.call': 'Appeler le {phone}',
  'walkin.orWalkIn': 'Ou venez sans rendez-vous aujourd’hui : {hours}.',
  'waitlist.cta': 'Aucune ne convient ? Inscrivez-vous sur la liste d’attente',
  'waitlist.heading': 'Liste d’attente : {service}',
  'waitlist.lead': 'Dites-nous quand vous pouvez venir. Dès qu’une place se libère, nous vous l’offrirons.',
  'waitlist.onlyLead': 'Ce service ne se réserve pas en ligne : inscrivez-vous et nous vous offrirons une heure.',
  'waitlist.branch': 'Succursale',
  'waitlist.anyBranch': 'N’importe quelle succursale',
  'waitlist.days': 'Jours où vous pouvez venir',
  'waitlist.daysHelp': 'Ne cochez rien pour n’importe quel jour.',
  'waitlist.parts': 'Moments de la journée',
  'waitlist.partsHelp': 'Ne cochez rien pour n’importe quand.',
  'waitlist.part.morning': 'Matin (avant midi)',
  'waitlist.part.afternoon': 'Après-midi (de midi à 17 h)',
  'waitlist.part.evening': 'Soir (après 17 h)',
  'waitlist.until': 'Me garder sur la liste jusqu’au',
  'waitlist.untilHelp': 'Jusqu’à {days} jours.',
  'waitlist.contactHeading': 'Qui êtes-vous, et comment vous joindre ?',
  'waitlist.contactHelp': 'Indiquez un cellulaire, un courriel, ou les deux.',
  'waitlist.textOffers': 'Écrivez-moi par texto quand une place se libère',
  'waitlist.join': 'M’inscrire sur la liste',
  'waitlist.contactRequired': 'Indiquez un cellulaire ou un courriel.',
  'waitlist.doneHeading': 'Vous êtes sur la liste d’attente',
  'waitlist.doneLead': 'Nous vous offrirons une place pour {service} dès qu’une place qui vous convient se libère.',
  'waitlist.already': 'Vous étiez déjà sur cette liste. Rien n’a changé.',
  'waitlist.by.text': 'Nous vous écrirons par texto.',
  'waitlist.by.email': 'Nous vous écrirons par courriel.',
  'waitlist.by.text_and_email': 'Nous vous écrirons par texto et par courriel.',
  'waitlist.holdNote': 'Une place offerte ne vous est réservée que peu de temps : répondez vite.',
  'waitlist.manage': 'Voir ma place ou quitter la liste',
  'waitlist.when': 'Vos disponibilités',
  'waitlist.endsOn': 'Sur la liste jusqu’au',
  'waitlist.where': 'Succursale',
  'waitlist.anyTime': 'N’importe quand',
  'entry.heading': 'Votre place sur la liste d’attente',
  'entry.status.waiting': 'Vous êtes sur la liste d’attente.',
  'entry.status.offered': 'Nous vous avons offert une place. Vérifiez vos textos ou vos courriels.',
  'entry.status.paused': 'Votre place sur la liste d’attente est en pause.',
  'entry.status.booked': 'Vous avez réservé une heure : vous n’êtes plus sur la liste d’attente.',
  'entry.status.left': 'Vous avez quitté la liste d’attente.',
  'entry.status.expired': 'Votre place sur la liste d’attente a pris fin.',
  'entry.status.removed': 'La pharmacie a retiré cette place de la liste d’attente.',
  'entry.keep': 'Rester sur la liste',
  'entry.pausedCall': 'Pour garder votre place, veuillez appeler la pharmacie.',
  'entry.leave': 'Quitter la liste d’attente',
  'entry.leaveQuestion': 'Quitter la liste d’attente ? Nous ne vous offrirons plus de place.',
  'entry.leaveConfirm': 'Oui, quitter la liste',
  'entry.stay': 'Rester sur la liste',
  'entry.missing': 'Ce lien n’est plus valide.',
  'offer.heading.held': 'Une heure vous est réservée',
  'offer.heading.group': 'Une heure vient de se libérer',
  'offer.heading.closed': 'Votre offre de la liste d’attente',
  'offer.heldUntil': 'Nous la gardons pour vous jusqu’à {until}. Réservez-la ou refusez-la d’ici là.',
  'offer.groupLead':
    'Nous avons offert cette heure à quelques personnes de la liste d’attente. La première à réserver l’obtient, jusqu’à {until}.',
  'offer.lastMinute': 'Il reste moins d’une minute.',
  'offer.questionsFirst': 'Cette visite comporte quelques questions avant la réservation.',
  'offer.answerQuestions': 'Répondre aux questions',
  'offer.book': 'Réserver',
  'offer.decline': 'Non merci',
  'offer.formsFailed': 'Nous n’avons pas pu charger les questions de cette visite.',
  'offer.retry': 'Réessayer',
  'offer.formsCall': 'Veuillez appeler la pharmacie pour réserver cette heure.',
  'offer.consentNeeded': 'Veuillez lire et accepter la déclaration de consentement avant de réserver.',
  'offer.backToOffer': 'Retour à l’offre',
  'offer.closed.booked': 'Vous avez réservé cette heure.',
  'offer.closed.declined': 'Vous avez refusé cette heure.',
  'offer.closed.lapsed': 'Cette offre a pris fin.',
  'offer.closed.taken': 'Quelqu’un d’autre a réservé cette heure en premier.',
  'offer.closed.withdrawn': 'Cette offre a été retirée.',
  'offer.stillOn': 'Vous êtes toujours sur la liste d’attente.',
  'offer.left': 'Vous avez quitté la liste d’attente. Nous ne vous offrirons plus de place.',
  'service.duration': '{minutes} minutes',
  'service.none': "Cette pharmacie n'accepte pas de rendez-vous en ligne pour le moment.",
  'service.englishOnly': '(en anglais)',
  'service.methodInPerson': 'En personne',
  'service.methodPhone': 'Par téléphone',
  'service.methodVirtual': 'Appel vidéo',
  'service.methodWalkIn': 'Sans rendez-vous',
  'service.search': 'Rechercher un service',
  'service.searchPlaceholder': 'Rechercher un service ou un besoin',
  'service.searchClear': 'Effacer la recherche',
  'service.noMatch': 'Aucun service ne correspond à « {query} ».',
  'service.countOne': '1 service',
  'service.countMany': '{count} services',
  'service.category.vaccination': 'Vaccins et injections',
  'service.category.travel': 'Santé-voyage',
  'service.category.minor_ailment': 'Affections mineures',
  'service.category.medication_review': 'Bilans de médicaments',
  'service.category.testing': 'Tests et dépistage',
  'service.category.consult': 'Consultations',
  'service.category.other': 'Autres services',

  'location.heading': 'Quelle succursale ?',

  'time.heading': 'Choisissez une heure',
  'time.headingFor': 'Choisissez une heure pour {service}',
  'time.nextAvailable': 'Prochaine disponibilité : {when}',
  'time.noneSoon': 'Aucune heure disponible dans les 14 prochains jours.',
  'time.noneSoonCall':
    'Aucune heure disponible dans les 14 prochains jours. Téléphonez au {phone} pour réserver.',
  'time.loadFailed': 'Impossible de charger les heures.',
  'time.date': 'Date',
  'time.dateHelp': 'Saisissez une date ou utilisez le calendrier.',
  'time.loading': 'Recherche des heures disponibles…',
  'time.none': 'Aucune heure disponible ce jour-là. Essayez une autre date.',
  'time.available': '{count} heures disponibles',
  'time.availableOne': '1 heure disponible',
  'time.timezoneNote': 'Heures affichées pour {location}.',
  'time.selected': '{time} sélectionnée',
  'time.gridLabel':
    'Heures disponibles. Utilisez les touches fléchées pour vous déplacer.',
  'time.chooseDay': 'Choisissez un jour',
  'time.earlierDays': 'Jours précédents',
  'time.laterDays': 'Jours suivants',
  'time.today': 'Aujourd’hui',
  'time.dayOpen': '{day}, des heures sont libres',
  'time.dayClosed': '{day}, aucune heure libre',
  'time.part.morning': 'Matin',
  'time.part.afternoon': 'Après-midi',
  'time.part.evening': 'Soir',

  'details.heading': 'Pour qui est ce rendez-vous ?',
  'details.aboutYou': 'À propos de vous',
  'details.reachYou': 'Pour vous joindre',
  'details.firstName': 'Prénom',
  'details.lastName': 'Nom',
  'details.dob': 'Date de naissance',
  'details.dobHelp': "Certains services exigent un âge minimum.",
  'details.email': 'Courriel',
  'details.emailHelp': 'Pour que la pharmacie puisse vous joindre au sujet de ce rendez-vous.',
  'details.phone': 'Numéro de mobile',
  'details.province': 'Province',
  'details.healthCard': "Numéro d'assurance maladie (facultatif)",
  'details.healthCardHelp':
    'Conservé de façon sécurisée. Vous pouvez aussi apporter votre carte.',
  'details.required': 'obligatoire',
  'details.holdTimer':
    'Votre heure est réservée encore moins d’une minute. Confirmez maintenant pour la garder.',
  'details.holdExpired': 'Votre heure a été libérée. Veuillez en choisir une autre.',
  'details.textReminders': 'Écrivez-moi par texto à ce numéro pour me rappeler mes rendez-vous',
  'details.textRemindersAgree': 'En cochant la case, vous acceptez ce qui suit :',

  'consent.heading': 'Consentement',
  'consent.agree': "J'ai lu et j'accepte ce qui précède.",

  'review.heading': 'Vérifiez votre rendez-vous',
  'review.confirm': 'Confirmer le rendez-vous',
  'review.change': 'Modifier',
  'review.submitting': 'Réservation…',
  'review.who': 'Qui',
  'review.contact': 'Coordonnées',
  'review.consent': 'Vous avez accepté',
  'review.texts': 'Rappels par texto',
  'review.textsYes': 'Oui, au {phone}',
  'review.textsNo': 'Non',
  'review.minutes': '{minutes} min.',
  'review.forms': 'Vos réponses',
  'review.formAnswered': 'Répondu',
  'review.formSkipped': 'Passé : nous vous enverrons un lien après la réservation',
  'review.formChange': 'Modifier vos réponses à {form}',
  'review.formsGo': 'Vos réponses sont transmises à {pharmacy} avec votre rendez-vous.',
  'review.formsChanged':
    'Les questions pour ce service ont changé. Veuillez y répondre avant de réserver.',
  'review.formsUnavailable':
    'Les formulaires ne peuvent pas être reçus pour l’instant. Vous pouvez quand même réserver; la pharmacie vous les enverra plus tard.',
  'questions.formOf': 'Formulaire {current} sur {total}',
  'questions.required': 'Requis pour réserver en ligne',
  'questions.optional': 'Facultatif',
  'questions.optionalIntro':
    'Ce formulaire est facultatif. Vous pouvez y répondre maintenant, ou le passer : nous vous enverrons un lien après la réservation.',
  'questions.answerNow': 'Répondre maintenant',
  'questions.skip': 'Passer pour l’instant',
  'questions.continue': 'Continuer',
  'questions.otherService': 'Choisir un autre service',
  'questions.loading': 'Chargement des questions pour ce service…',
  'questions.loadFailed': 'Nous n’avons pas pu charger les questions pour ce service.',
  'questions.retry': 'Réessayer',
  'questions.timedOut':
    'Pour votre confidentialité, vos réponses ont été effacées après 20 minutes sans activité. Veuillez recommencer.',
  'error.summary': 'Veuillez corriger ce qui suit :',

  'done.heading': 'Votre rendez-vous est confirmé',
  'done.when': 'Quand',
  'done.where': 'Où',
  'done.what': 'Quoi',
  'done.emailed': 'Nous avons envoyé une confirmation à {email}.',
  'done.saveThis':
    "Enregistrez cette page ou faites une capture d'écran — elle contient les détails de votre rendez-vous.",
  'done.manageLink': 'Modifier ou annuler',

  'manage.heading': 'Votre rendez-vous',
  'manage.reschedule': "Changer l'heure",
  'manage.rescheduleHeading': 'Choisissez une nouvelle heure',
  'manage.useThisTime': 'Choisir cette heure',
  'manage.keepTime': 'Garder mon heure',
  'manage.moved': 'C’est fait — votre rendez-vous est maintenant {when}.',
  'manage.cancel': 'Annuler le rendez-vous',
  'manage.cancelConfirm': 'Annuler ce rendez-vous ? Cette action est irréversible.',
  'manage.cancelReason': 'Motif (facultatif)',
  'manage.cancelled': 'Ce rendez-vous est annulé.',
  'manage.completed': 'Ce rendez-vous est terminé.',
  'manage.noReschedule':
    'Pour modifier ce rendez-vous, veuillez téléphoner à la pharmacie.',
  'manage.keepIt': 'Le conserver',

  'form.loading': 'Ouverture de votre formulaire…',
  'form.dobHeading': 'Entrez votre date de naissance',
  'form.dobIntro':
    'Pour protéger vos réponses, nous vérifions votre date de naissance avant de vous montrer le formulaire.',
  'form.dobLegend': 'Date de naissance',
  'form.dobHint': 'Par exemple, 23 4 1980',
  'form.dobDay': 'Jour',
  'form.dobMonth': 'Mois',
  'form.dobYear': 'Année',
  'form.dobInvalid': 'Entrez votre date de naissance, avec un jour, un mois et une année.',
  'form.dobMismatch': 'Cette date de naissance ne correspond pas à celle de la pharmacie.',
  'form.triesLeft': 'Il vous reste {count} essais.',
  'form.triesLeftOne': 'Il vous reste 1 essai.',
  'form.continue': 'Continuer',
  'form.sentHeading': 'Merci, vos réponses ont été envoyées',
  'form.sentBody': '{pharmacy} a reçu vos réponses. Vous pouvez fermer cette page.',
  'form.nextForm': 'Remplir le formulaire suivant',
  'form.nextFormIntro': 'Votre visite comporte un autre formulaire.',
  'form.timeoutHeading': 'Êtes-vous toujours là ?',
  'form.timeoutBody':
    'Pour protéger vos renseignements, ce formulaire se fermera dans 2 minutes sans activité. Vos réponses sont enregistrées.',
  'form.keepGoing': 'Continuer',
  'form.closeNow': 'Fermer le formulaire',
  'form.closedHeading': 'Ce formulaire a été fermé pour protéger vos renseignements',
  'form.closedBody': 'Vos réponses sont enregistrées. Rouvrez le formulaire pour continuer.',
  'form.openAgain': 'Rouvrir le formulaire',
  'form.otherDevice':
    'Ce formulaire a été ouvert sur un autre appareil. Rouvrez-le ici pour continuer.',
  'form.updated':
    'La pharmacie a mis ce formulaire à jour, il a donc été rechargé. Vérifiez vos réponses.',
  'form.saveFailed':
    "Nous n'avons pas pu enregistrer votre dernière réponse. Nous réessaierons à la question suivante.",
  'form.callPhone': 'Des questions ? Appelez le {phone}.',
  'form.callForLink': 'Appelez le {phone} pour obtenir un nouveau lien.',
  'form.callPharmacy': 'Veuillez appeler la pharmacie.',
  'form.state.submitted': 'Vous avez déjà envoyé ce formulaire. Merci.',
  'form.state.expired': 'Ce lien a expiré.',
  'form.state.locked': 'Pour protéger vos renseignements, ce lien est verrouillé après trop d’essais.',
  'form.state.cancelled': "La pharmacie n'a plus besoin de ce formulaire.",
  'form.state.not_needed':
    "Ce formulaire n'est plus nécessaire : le rendez-vous qu'il concernait a été annulé.",
  'form.state.replaced': 'Un lien plus récent a été envoyé pour ce formulaire. Utilisez le dernier.',
  'form.state.used': 'Ce code a déjà été utilisé sur un autre téléphone.',
  'form.state.restart':
    'Pour protéger vos renseignements, ce formulaire a été fermé. Rouvrez le lien du formulaire de la pharmacie pour recommencer.',
  'form.state.unavailable': "Ce formulaire n'est pas disponible pour le moment.",
  'form.state.missing': 'Nous ne trouvons pas ce formulaire. Vérifiez le lien.',
  'form.publicIntro':
    'Dites-nous d’abord qui vous êtes, pour que la pharmacie ajoute vos réponses à votre dossier.',
  'form.contactHint':
    'Indiquez un numéro de cellulaire ou un courriel, ou les deux, pour que la pharmacie puisse vous joindre.',
  'form.contactRequired': 'Indiquez un numéro de cellulaire ou un courriel.',
  'form.start': 'Commencer le formulaire',
  'form.spamCheck': "Confirmez que vous n'êtes pas un robot",
  'form.spamFailed': "Veuillez confirmer que vous n'êtes pas un robot.",
  'form.dailyCap':
    "Ce formulaire n'accepte plus de réponses aujourd'hui. Veuillez appeler la pharmacie.",
  'form.notAccepted': "Ce formulaire n'a pas pu être commencé. Veuillez appeler la pharmacie.",
  'form.dueHeading': 'Avant votre visite',
  'form.dueIntro': 'La pharmacie vous demande de remplir un formulaire avant votre rendez-vous.',
  'form.dueIntroMany':
    'La pharmacie vous demande de remplir ces formulaires avant votre rendez-vous.',
  'form.fillIn': 'Remplir le formulaire',
  'form.carryOn': 'Continuer le formulaire',
  'form.dueRequired': 'À remplir avant votre visite',
  'form.dueOptional': 'Facultatif',
  'form.dueSent': 'Envoyé',
  'form.dueClosed': "N'est plus ouvert",
  'form.sentWithBooking': 'Envoyé avec votre rendez-vous',
  'form.formsHeading': 'Vos formulaires',
  'form.stopAfterHeading': 'Veuillez lire ceci',
  'form.stopAfterCall': 'Des questions? Appelez le {phone}.',

  'error.missing': "Nous n'avons pas trouvé cette page.",
  'error.notTaking': "Cette pharmacie n'accepte pas de rendez-vous en ligne pour le moment.",
  'error.missingHelp':
    "Vérifiez l'adresse ou communiquez directement avec la pharmacie.",
  'error.offline': "Impossible de joindre la pharmacie. Vérifiez votre connexion.",
  'error.slotGone': "Cette heure vient d'être prise. Veuillez en choisir une autre.",
  'error.slotGoneKept':
    'Cette heure a été prise pendant que vous répondiez. Vos réponses sont conservées : veuillez choisir une autre heure.',
  'error.unknown': "Une erreur s'est produite. Veuillez réessayer.",
  'error.retry': 'Réessayer',
  'error.fieldRequired': 'Ce champ est obligatoire.',
  'error.emailInvalid': 'Saisissez une adresse comme nom@exemple.com.',
  'error.phoneInvalid': 'Saisissez un numéro de mobile où vous joindre.',
  'error.dobInvalid': 'Saisissez une date de naissance comme 1985-04-12.',
  'error.dobFuture': 'Cette date est dans le futur.',
  'error.consentRequired': 'Veuillez accepter le consentement pour continuer.',
  // Track CGS — parties (CGS-D9 to D14) et cliniques (CGS-D2, D3)
  'people.heading': 'Combien de personnes ?',
  'people.help': 'Réservez pour vous, ou pour vous et les personnes qui vous accompagnent, reçues l’une après l’autre.',
  'people.justMe': 'Moi seulement',
  'people.count': '{count} personnes',
  'people.together': 'Environ {minutes} minutes en tout',
  'people.eachOwn': 'Vous indiquerez les renseignements et les réponses de chaque personne, et un seul courriel et un seul cellulaire pour tous.',
  'party.timeHeading': 'Choisissez une heure pour {count} personnes : {service}',
  'party.personStep': 'Personne {n}',
  'party.contactStep': 'Coordonnées',
  'party.personHeading': 'Personne {n} sur {total}',
  'party.firstPerson': 'Commencez par vous, ou par la première personne qui vient.',
  'party.nextPerson': 'Qui d’autre vient ?',
  'party.questionsFor': 'Pour {name}, personne {n} sur {total}',
  'party.samePerson': 'Cette personne est déjà dans la réservation.',
  'party.timeGone': 'Cette heure vient d’être prise. Choisissez-en une autre : les renseignements de chacun sont conservés.',
  'party.aboutPerson': 'Au sujet de la personne {n} : {message}',
  'party.contactHeading': 'Comment vous joindre ?',
  'party.contactHelp': 'Un seul courriel et un seul cellulaire pour tous : la confirmation et les rappels y sont envoyés.',
  'party.answersGiven': '{name} : {count} formulaire(s) rempli(s)',
  'party.oneEmail': 'Une seule confirmation, pour tous, est envoyée au courriel ci-dessus.',
  'party.confirm': 'Réserver pour {count} personnes',
  'party.bookedFor': 'Réservé pour {count} personnes',
  'clinic.runsOn': 'Dates de cette clinique',
  'clinic.noDatesLeft': 'Cette clinique n’a plus de dates.',
  'clinic.dayFull': 'Complet',
  'clinic.walkIns': 'Sans rendez-vous bienvenus',
  'clinic.beforeYouCome': 'Avant de venir',
  'clinic.full': 'Cette clinique est complète. Appelez la pharmacie, ou revenez voir s’il y a une annulation.',
  'clinic.listHeading': 'Cliniques',
  'clinic.next': 'Prochaine date : {date}',
  'manage.partyHeading': 'Dans cette réservation ({count})',
  'manage.personBooked': 'Réservé',
  'manage.personCancelled': 'Annulé',
  'manage.personMoved': 'Déplacé à une autre heure',
  'manage.cancelPerson': 'Annuler pour {name}',
  'manage.cancelPersonConfirm': 'Annuler le rendez-vous de {name} ? Les autres gardent le leur.',
  'manage.cancelAll': 'Tout annuler',
  'manage.cancelAllConfirm': 'Annuler les rendez-vous de tous ? C’est définitif.',
  'manage.moveAll': 'Déplacer tout le monde',
  'manage.partyCancelled': 'Tous les rendez-vous de cette réservation sont annulés.',
  // Track ORX — demandes de renouvellement, de prolongation et de transfert (ORX-D1 à D13)
  'refill.title': 'Demander un renouvellement, une prolongation ou un transfert',
  'refill.loading': 'Chargement…',
  'refill.step.who': 'Vous',
  'refill.step.code': 'Code reçu',
  'refill.step.request': 'Demande',
  'refill.step.done': 'Terminé',
  'refill.whoLead': 'Dites-nous d’abord qui vous êtes. Nous enverrons un code à 6 chiffres par texto à votre cellulaire pour confirmer que c’est bien vous.',
  'refill.mobileHelp': 'Nous enverrons votre code par texto à ce numéro.',
  'refill.sendCode': 'M’envoyer un code',
  'refill.tooMany': 'Trop de codes ont été demandés. Veuillez patienter un moment et réessayer, ou appelez-nous.',
  'refill.checkDetails': 'Veuillez vérifier vos renseignements et réessayer.',
  'refill.codeHeading': 'Entrez votre code',
  'refill.codeSentTo': 'Nous avons envoyé un code à 6 chiffres par texto au {phone}. Il expire dans 10 minutes.',
  'refill.codeLabel': 'Code à 6 chiffres',
  'refill.codeHint': 'Tapez ou collez le code reçu par texto.',
  'refill.codeIncomplete': 'Entrez les 6 chiffres du code.',
  'refill.wrongCode': 'Ce code n’est pas le bon. Il vous reste {count} essais.',
  'refill.wrongCodeOne': 'Ce code n’est pas le bon. Il vous reste 1 essai.',
  'refill.wrongCodeAgain': 'Ce code n’est pas le bon. Veuillez réessayer.',
  'refill.resend': 'M’envoyer un nouveau code',
  'refill.resendIn': 'Vous pourrez demander un nouveau code dans {seconds} secondes.',
  'refill.resent': 'Nous vous avons envoyé un nouveau code.',
  'refill.noMoreCodes': 'Nous ne pouvons plus envoyer de code pour cette demande. Recommencez plus tard, ou appelez-nous.',
  'refill.changeDetails': 'Modifier mes renseignements',
  'refill.locked': 'Trop de codes erronés : ce code est bloqué.',
  'refill.expired': 'Ce code a expiré.',
  'refill.sessionExpired': 'Cette demande a pris trop de temps et a expiré. Rien n’a été envoyé.',
  'refill.startAgainHelp': 'Recommencez et nous vous enverrons un nouveau code.',
  'refill.startAgain': 'Recommencer',
  'refill.requestHeading': 'De quoi avez-vous besoin?',
  'refill.kindLegend': 'Type de demande',
  'refill.kind.refill': 'Un renouvellement',
  'refill.kind.refillHelp': 'D’autres doses d’une ordonnance que vous faites déjà exécuter chez nous.',
  'refill.kind.renewal': 'Une prolongation',
  'refill.kind.renewalHelp': 'Une ordonnance qui n’a plus de renouvellements, à prolonger.',
  'refill.kind.transfer': 'Un transfert',
  'refill.kind.transferHelp': 'Transférer une ordonnance chez nous depuis une autre pharmacie.',
  'refill.kindRequired': 'Choisissez un renouvellement, une prolongation ou un transfert.',
  'refill.items.refill': 'Numéros d’ordonnance ou noms des médicaments',
  'refill.items.refillHelp': 'Le numéro d’ordonnance figure sur votre étiquette. Un par case.',
  'refill.items.renewal': 'Médicaments à prolonger',
  'refill.items.renewalHelp': 'Le nom du médicament, ou son numéro d’ordonnance. Un par case.',
  'refill.items.transfer': 'Médicaments à transférer',
  'refill.items.transferHelp': 'Le nom du médicament, ou son numéro d’ordonnance à l’autre pharmacie. Un par case.',
  'refill.item.refill': 'Ordonnance ou médicament {n}',
  'refill.item.renewal': 'Médicament à prolonger {n}',
  'refill.item.transfer': 'Médicament à transférer {n}',
  'refill.itemRemove': 'Retirer le numéro {n}',
  'refill.itemAdd': 'En ajouter un autre',
  'refill.itemsMax': 'Vous pouvez en demander jusqu’à 10 à la fois.',
  'refill.itemsRequired': 'Ajoutez au moins un numéro d’ordonnance ou un médicament.',
  'refill.fromName': 'Pharmacie où elle se trouve',
  'refill.fromPhone': 'Numéro de téléphone de cette pharmacie, si vous le connaissez',
  'refill.prescriber': 'Prescripteur, si vous le connaissez',
  'refill.neededBy': 'Pour quand en avez-vous besoin?',
  'refill.optional': 'Facultatif.',
  'refill.neededByPast': 'Choisissez aujourd’hui ou une date ultérieure.',
  'refill.neededByInvalid': 'Entrez une date comme 2026-11-05.',
  'refill.note': 'Autre chose à nous dire?',
  'refill.noteHelp': 'Facultatif. N’indiquez pas de numéro de carte santé.',
  'refill.noteCount': '{count} sur {max} caractères',
  'refill.noteTooLong': 'La note doit compter 500 caractères ou moins.',
  'refill.textWhenReady': 'Écrivez-moi par texto quand ce sera prêt',
  'refill.send': 'Envoyer ma demande',
  'refill.checkRequest': 'Veuillez vérifier votre demande et réessayer.',
  'refill.doneHeading': 'Nous avons envoyé votre demande à {pharmacy}',
  'refill.reference': 'Votre numéro de demande',
  'refill.doneText': 'Nous vous écrirons par texto quand ce sera prêt. Appelez-nous si vous avez une question.',
  'refill.doneCall': 'Nous vous appellerons si nous avons une question. Appelez-nous si vous en avez une.',
  'refill.unavailableHeading': 'Les demandes en ligne ne sont pas offertes ici',
  'refill.callUsAt': 'Pour demander un renouvellement, une prolongation ou un transfert, appelez-nous au',
  'refill.callUs': 'Pour demander un renouvellement, une prolongation ou un transfert, veuillez appeler la pharmacie.',
  // Suivis de Track ORX (2026-10-29) — sans code par texto (ORX-D15 à D19), le courriel (ORX-D20), et
  // les refus du début dits près de leur champ
  'refill.whoLeadNoCode': 'Dites-nous d’abord qui vous êtes.',
  'refill.mobileHelpNoCode': 'Nous utiliserons ce numéro si nous devons vous joindre au sujet de votre demande.',
  'refill.phoneCheck': 'Votre numéro de cellulaire : {phone}. Est-ce exact?',
  'refill.changeNumber': 'Modifier le numéro',
  'refill.email': 'Courriel (facultatif)',
  'refill.emailHelp':
    'Nous vous écrirons par courriel pour confirmer la réception de votre demande, puis quand elle sera prête.',
  'refill.emailInvalid': 'Entrez une adresse courriel comme nom@exemple.com.',
  'refill.doneEmailed': 'Nous avons envoyé une copie à {email}.',
  'refill.nameMissing': 'Entrez votre prénom et votre nom.',
  'refill.nameTooLong': 'Ce nom est trop long.',
  'refill.phoneInvalid': 'Entrez un numéro de cellulaire canadien ou américain qui peut recevoir des textos.',
  'refill.tooManyNoCode': 'Trop de demandes ont été commencées. Veuillez patienter un moment et réessayer, ou appelez-nous.',
  'refill.startAgainHelpNoCode': 'Vos renseignements sont toujours remplis : cela ne prendra qu’un instant.',
  // ORX-D24 — un cellulaire qui a déjà répondu STOP à la pharmacie (libellé à confirmer)
  'refill.textsStoppedCode':
    'Nous ne pouvons pas envoyer de code par texto à ce numéro. Si vous avez répondu STOP à {pharmacy}, répondez START (ou DÉBUT) à leur dernier texto, puis réessayez, ou utilisez un autre numéro de cellulaire.',
  'refill.textsStoppedNote':
    'Nous ne pouvons pas envoyer de textos à ce numéro. Si vous avez répondu STOP à {pharmacy}, répondez START (ou DÉBUT) à leur dernier texto. Pour savoir quand ce sera prêt, ajoutez votre courriel ci-dessous.',
  'refill.doneEmailWhenReady':
    'Nous vous écrirons par courriel quand ce sera prêt. Appelez-nous si vous avez une question.',
  // Track CIQ — l’arrivée à la pharmacie (l’affiche et la borne du comptoir), « Me voici » dans le lien
  // de gestion, la place du patient dans la file d’attente et l’écran de la salle d’attente
  'checkIn.missing': 'Ce lien d’enregistrement ne fonctionne pas. Veuillez vous présenter au comptoir.',
  'checkIn.heading': 'Signalez votre arrivée : {place}',
  'checkIn.lead': 'Dites-nous que vous êtes là, et nous vous garderons une place dans la file d’attente.',
  'checkIn.haveAppointment': 'J’ai un rendez-vous',
  'checkIn.haveAppointmentHelp': 'Trouvez votre rendez-vous avec votre nom de famille et votre date de naissance.',
  'checkIn.walkingIn': 'Je viens sans rendez-vous',
  'checkIn.walkingInHelp': 'Pas de rendez-vous ? Prenez place dans la file d’attente.',
  'checkIn.noWalkIns': 'Pas de rendez-vous ? Veuillez vous présenter au comptoir.',
  'checkIn.find': 'Trouver mon rendez-vous',
  'checkIn.findLead': 'Entrez le nom de famille et la date de naissance de la personne qui a le rendez-vous.',
  'checkIn.notFound': 'Nous n’avons trouvé aucun rendez-vous aujourd’hui avec ces renseignements.',
  'checkIn.seeCounter': 'Veuillez vous présenter au comptoir.',
  'checkIn.tooEarly': 'L’enregistrement ouvre à {time}, une heure avant le rendez-vous.',
  'checkIn.tooLate': 'L’enregistrement est fermé pour ce rendez-vous. Veuillez vous présenter au comptoir.',
  'checkIn.expired': 'Le délai est écoulé. Veuillez chercher votre rendez-vous de nouveau.',
  'checkIn.foundHeading': 'Nous avons trouvé votre rendez-vous',
  'checkIn.whoIsHere': 'Qui est ici ?',
  'checkIn.alreadyIn': 'Arrivée déjà enregistrée',
  'checkIn.allIn': 'L’arrivée de tout le monde est déjà enregistrée.',
  'checkIn.imHere': 'Me voici',
  'checkIn.textMe': 'Écrivez-moi par texto quand ce sera mon tour',
  'checkIn.textTo': 'Au cellulaire se terminant par {digits}.',
  'checkIn.walkInHeading': 'Prendre place dans la file d’attente',
  'checkIn.walkInLead': 'Dites-nous qui vous êtes, et nous vous ajouterons à la file d’attente.',
  'checkIn.serviceLegend': 'Pour quel service venez-vous ?',
  'checkIn.serviceRequired': 'Choisissez le service pour lequel vous venez.',
  'checkIn.questionsFirst': 'Veuillez répondre à quelques questions avant de prendre place dans la file d’attente.',
  'checkIn.formsGo': 'Vos réponses sont transmises à {pharmacy} quand vous prenez place dans la file d’attente.',
  'checkIn.formSkipped': 'Passé',
  'checkIn.formOptionalIntro': 'Ce formulaire est facultatif. Vous pouvez y répondre maintenant, ou le passer.',
  'checkIn.join': 'Prendre place dans la file',
  'checkIn.walkInsClosed':
    'Nous ne prenons pas de visites sans rendez-vous pour le moment. Veuillez vous présenter au comptoir.',
  'checkIn.clinicFull': 'Cette clinique est complète aujourd’hui. Veuillez vous présenter au comptoir.',
  'checkIn.alreadyWaiting': 'Vous êtes déjà dans la file d’attente ici : numéro {ticket}.',
  'checkIn.phoneInvalid': 'Entrez un numéro de cellulaire canadien ou américain.',
  'checkIn.formsChanged':
    'Les questions pour cette visite ont changé. Veuillez y répondre avant de prendre place dans la file d’attente.',
  'checkIn.formsUnavailable':
    'Nous ne pouvons pas recevoir vos réponses pour l’instant. Vous pouvez quand même prendre place dans la file d’attente; la pharmacie passera les questions en revue avec vous.',
  'checkIn.visitChanged': 'Ce rendez-vous vient de changer. Veuillez vous présenter au comptoir.',
  'checkIn.unavailable': 'L’enregistrement n’est pas ouvert pour ce rendez-vous. Veuillez vous présenter au comptoir.',
  'checkIn.busy':
    'Beaucoup de personnes signalent leur arrivée en ce moment. Veuillez réessayer dans une minute, ou vous présenter au comptoir.',
  'checkIn.startOver': 'Recommencer',
  'checkIn.checkedIn': 'Votre arrivée est enregistrée.',
  'checkIn.number': 'Votre numéro : {ticket}',
  'checkIn.numbers': 'Vos numéros : {tickets}',
  'checkIn.kioskNumber': 'Votre numéro : {ticket} — veuillez prendre place.',
  'checkIn.kioskNumbers': 'Vos numéros : {tickets} — veuillez prendre place.',
  'checkIn.ahead': '{n} personnes devant vous',
  'checkIn.aheadOne': '1 personne devant vous',
  'checkIn.next': 'Vous êtes la prochaine personne',
  'checkIn.follow': 'Suivre ma place dans la file',
  'checkIn.manageHeading': 'Signaler votre arrivée',
  'checkIn.manageLead': 'Vous êtes à la pharmacie ? Dites-nous que vous êtes là.',
  'queue.loading': 'Recherche de votre place dans la file d’attente…',
  'queue.heading': 'Votre place dans la file d’attente',
  'queue.missing': 'Ce lien n’est plus actif.',
  'queue.called': 'C’est votre tour — veuillez vous présenter au comptoir.',
  'queue.serving': 'Vous êtes en consultation.',
  'queue.done': 'Votre visite est terminée. Merci !',
  'queue.left': 'Vous avez quitté la file d’attente.',
  'queue.leave': 'Quitter la file d’attente',
  'queue.leaveQuestion':
    'Quitter la file d’attente ? Vous perdrez votre place, et votre visite d’aujourd’hui sera annulée.',
  'queue.leaveConfirm': 'Oui, quitter la file',
  'queue.stay': 'Rester dans la file',
  'queue.updates': 'Cette page se met à jour toute seule.',
  'lobby.loading': 'Chargement…',
  'lobby.nowCalling': 'Numéros appelés',
  'lobby.next': 'À suivre',
  'lobby.nobodyCalled': 'Aucun numéro appelé pour le moment',
  'lobby.nobodyWaiting': 'Personne n’attend',
  'lobby.missing': 'Le lien de cet écran a été remplacé. Demandez le nouveau lien au personnel.',
};

const CATALOG: Record<Locale, Record<MessageKey, string>> = { en, fr };

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
};

export function isLocale(value: string | null | undefined): value is Locale {
  return LOCALES.includes(value as Locale);
}

type Vars = Record<string, string | number>;
export type Translate = (key: MessageKey, vars?: Vars) => string;

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
  /** Which languages this pharmacy actually offers. */
  available: Locale[];
}

const I18nContext = createContext<I18nValue | null>(null);

/**
 * `{name}` interpolation and nothing else.
 *
 * No plural engine: the two places that need one pick the singular key
 * explicitly, which is clearer than a rules table for two languages that share
 * the same simple plural rule.
 */
function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export function I18nProvider({
  children,
  available = [...LOCALES],
  initial,
  fallback,
}: {
  children: ReactNode;
  available?: Locale[];
  /** An explicit choice — the `?lang=` a pharmacy's own page passes in. Wins outright. */
  initial?: Locale;
  /** The pharmacy's default, used when the browser asks for neither language. */
  fallback?: Locale;
}) {
  const offered = available.length > 0 ? available : (['en'] as Locale[]);
  const [locale, setLocaleState] = useState<Locale>(
    () => initial ?? pickInitial(offered, fallback),
  );
  // One function for the provider's life. A page may list it in an effect's dependencies: the form
  // page did, and when it changed with every new language list, the page opened its link again and
  // again and the patient only ever saw "Opening your form…".
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    document.documentElement.lang = next;
  }, []);

  const value = useMemo<I18nValue>(() => {
    // A pharmacy can stop offering a language while somebody is mid-booking.
    const active = offered.includes(locale) ? locale : offered[0]!;
    return {
      locale: active,
      available: offered,
      setLocale,
      t: (key, vars) => interpolate(CATALOG[active][key], vars),
    };
  }, [locale, offered, setLocale]);

  // `<html lang>` follows the language actually on screen — from the first
  // render, not only after a switch — so a screen reader uses the right voice
  // and the browser offers the right translation (A5 FE-P-01).
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = value.locale;
  }, [value.locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * The browser's preference when it is English or French and the pharmacy offers
 * it; otherwise the pharmacy's default (Q11).
 *
 * A patient whose phone is set to French should land on French without having
 * to find a switch — and the switch is still there when the guess is wrong.
 */
export function pickInitial(offered: Locale[], fallback?: Locale): Locale {
  for (const tag of typeof navigator === 'undefined' ? [] : navigator.languages ?? []) {
    const base = tag.split('-')[0]?.toLowerCase();
    if (isLocale(base) && offered.includes(base)) return base;
  }
  if (fallback && offered.includes(fallback)) return fallback;
  return offered[0]!;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside an I18nProvider');
  return value;
}

/** Exported for the test that proves the two catalogs stay in step. */
export const CATALOGS_FOR_TEST = CATALOG;
